import { useState } from "react";
import { Link, useSearchParams } from "react-router";
import { api } from "@/lib/api";
import { Wordmark } from "@/components/Wordmark";
import { SIGNIN } from "@/constants/testIds";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { ArrowLeft, Mail, ShieldCheck, ExternalLink, Info } from "lucide-react";

export default function SignIn() {
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState("idle");
  const [error, setError] = useState("");
  const [devLink, setDevLink] = useState("");
  const [params] = useSearchParams();
  const reason = params.get("reason");

  const reasonCopy = reason === "expired"
    ? "Your session expired. Please sign in again to continue where you left off."
    : reason === "revoked"
    ? "Your session was signed out. Please sign in again to continue."
    : null;

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setStatus("loading");
    setDevLink("");
    try {
      const { data } = await api.post("/auth/magic/request", { email: email.trim().toLowerCase() });
      if (data.dev_link) setDevLink(data.dev_link);
      setStatus("sent");
    } catch (err) {
      setStatus("idle");
      setError(err?.response?.data?.detail || "Something went wrong. Please try again.");
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col">
      <div className="max-w-7xl w-full mx-auto px-6 pt-6">
        <Link to="/" className="inline-flex items-center gap-1.5 text-sm text-slate-600 hover:text-slate-900">
          <ArrowLeft className="w-4 h-4" /> Back
        </Link>
      </div>
      <div className="flex-1 flex items-center justify-center px-6 py-12">
        <div className="w-full max-w-md">
          <div data-testid={SIGNIN.container} className="fs-card p-8">
            <Wordmark className="text-base" />
            <h1 className="mt-6 font-outfit text-2xl font-bold text-slate-900">Sign in to Launchpad</h1>
            <p className="mt-1 text-sm text-slate-600">
              Enter your email — we'll send you a secure sign-in link. Access is invitation-only.
            </p>

            {reasonCopy && (
              <Alert data-testid="signin-reason-banner" className="mt-4 bg-blue-50 border-blue-200 text-blue-900">
                <Info className="w-4 h-4" />
                <AlertDescription>{reasonCopy}</AlertDescription>
              </Alert>
            )}

            {status !== "sent" && (
              <form onSubmit={submit} className="mt-6 space-y-4">
                <div>
                  <Label htmlFor="email">Work email</Label>
                  <Input
                    id="email"
                    type="email"
                    required
                    data-testid={SIGNIN.emailInput}
                    placeholder="you@company.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="mt-1"
                  />
                </div>
                {error && (
                  <Alert variant="destructive" data-testid={SIGNIN.errorAlert}>
                    <AlertDescription>{error}</AlertDescription>
                  </Alert>
                )}
                <Button
                  type="submit"
                  data-testid={SIGNIN.submitButton}
                  className="w-full fs-btn-primary"
                  disabled={status === "loading" || !email.trim()}
                >
                  <Mail className="w-4 h-4 mr-2" />
                  {status === "loading" ? "Sending..." : "Email me a sign-in link"}
                </Button>
              </form>
            )}

            {status === "sent" && (
              <div data-testid={SIGNIN.successCard} className="mt-6 space-y-4">
                <Alert>
                  <AlertDescription>
                    If an account exists for <b>{email}</b>, we've sent a secure sign-in link.
                    Please check your inbox. The link expires in 30 minutes.
                  </AlertDescription>
                </Alert>
                {devLink && (
                  <div className="fs-card bg-blue-50/50 border-blue-200 p-4 text-sm">
                    <div className="font-medium text-blue-900 mb-1">Dev mode link</div>
                    <p className="text-blue-800/80 mb-2 text-xs">
                      Emails may be limited in test mode. Use this link to continue:
                    </p>
                    <a
                      data-testid={SIGNIN.devLink}
                      href={devLink}
                      className="inline-flex items-center gap-1 text-blue-700 hover:text-blue-900 break-all"
                    >
                      {devLink} <ExternalLink className="w-3 h-3 flex-shrink-0" />
                    </a>
                  </div>
                )}
                <Button variant="outline" className="w-full" onClick={() => { setStatus("idle"); setEmail(""); }}>
                  Use a different email
                </Button>
              </div>
            )}

            <div className="mt-8 pt-6 border-t border-slate-200 flex items-start gap-3 text-xs text-slate-500">
              <ShieldCheck className="w-4 h-4 text-blue-600 flex-shrink-0 mt-0.5" />
              <div>
                We never ask for your CMS or website password. Access is limited, logged, and revocable.
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
